import { useMutation } from 'react-query';
import { useState } from 'react';
import { MetaCampaignReservationResult } from 'api/services/mediaPlanning/bookMetaPoll';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';
import usePlanStore from '../../../../../../store/planStore';
import { bookMetaStart } from '../../../../../../api/services/mediaPlanning/bookMetaStart';
import useBookMetaPoll from './useBookMetaPoll';

const useBookMetaStart = (getMetaPredictionStatuses: () => void) => {
  const { countryForms } = usePlanStore();
  const { showError } = useError();
  const { asyncBookMetaPollTrigger, isLoading: isPolling } = useBookMetaPoll(
    getMetaPredictionStatuses
  );
  const [pollingData, setPollingData] =
    useState<MetaCampaignReservationResult | null>(null);

  const isAnyMetaNotReserved = Object.values(countryForms).some(
    (country) => country.metaConfig?.reservationStatus !== 'reserved'
  );

  const {
    mutate: bookMetaStartTrigger,
    mutateAsync: asyncBookMetaStartTrigger,
    data: response,
    isLoading,
    isError,
    error,
  } = useMutation(
    async ({ planId }: { planId: string | null }) => {
      return bookMetaStart(planId);
    },
    {
      onError: (errorHandler: any) => {
        const errorMessage =
          errorHandler?.response?.data?.message ||
          errorHandler.message ||
          'Unknown error';
        showError(errorMessage);

        if (getMetaPredictionStatuses && isAnyMetaNotReserved) {
          getMetaPredictionStatuses();
        }
      },

      onSuccess: async (res) => {
        const startTime = Date.now();
        const pollInterval = 5000; // 30 seconds
        const maxDuration = 3 * 60 * 1000; // 3 minutes

        const poll = async () => {
          try {
            const result = await asyncBookMetaPollTrigger({
              jobId: res.data.jobId,
            });

            setPollingData(result);

            if (result?.data?.jobStatus === 'completed') {
              setPollingData(result);
              return;
            }

            if (Date.now() - startTime < maxDuration) {
              setTimeout(poll, pollInterval);
            }
          } catch (e) {
            // Shows default error
          }
        };

        poll();
      },
    }
  );

  return {
    bookMetaStartTrigger,
    asyncBookMetaStartTrigger,
    data: pollingData,
    isLoading,
    isError,
    error,
    isPolling,
  };
};

export default useBookMetaStart;
